import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Calendar, Check, ChevronDown } from 'lucide-react';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';

export type DateRange = 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'custom';

interface DateRangeFilterProps {
  dateRange: DateRange;
  startDate: string;
  endDate: string;
  onRangeChange: (range: DateRange) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  className?: string;
}

const formatDate = (dateStr: string, locale: string) => {
  const value = new Date(dateStr);
  if (Number.isNaN(value.getTime())) return dateStr;
  return value.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateRange,
  startDate,
  endDate,
  onRangeChange,
  onStartDateChange,
  onEndDateChange,
  className,
}) => {
  const { t, languageCode } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const locale = languageCode === 'id' ? 'id-ID' : 'en-GB';
  const ranges: { label: string; value: DateRange }[] = [
    { label: t('common.dateRange.thisWeek'), value: 'thisWeek' },
    { label: t('common.dateRange.thisMonth'), value: 'thisMonth' },
    { label: t('common.dateRange.lastMonth'), value: 'lastMonth' },
    { label: t('common.dateRange.thisQuarter'), value: 'thisQuarter' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={className ? `relative ${className}` : 'relative'} ref={dropdownRef} data-test-id="common-date-range-filter">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-4 px-5 py-2 bg-white border border-gray-200 rounded-2xl shadow-sm hover:border-gray-400 transition-all group"
        data-test-id="common-date-range-trigger"
      >
        <Calendar className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
        <div className="flex flex-col items-start">
          <span className="text-[12px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">{t('common.dateRange.label')}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900">
              {dateRange === 'custom' ? t('common.dateRange.customRange') : ranges.find(range => range.value === dateRange)?.label}
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-sm font-medium text-gray-500 italic">
              {formatDate(startDate, locale)} - {formatDate(endDate, locale)}
            </span>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <div
        className={`absolute right-0 mt-3 w-80 bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.12)] border border-gray-100 z-50 p-2 overflow-hidden transition-all duration-200 origin-top-right ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
        }`}
        data-test-id="common-date-range-dropdown"
      >
        <div className="space-y-1">
          {ranges.map(range => (
            <button
              key={range.value}
              type="button"
              onClick={() => {
                onRangeChange(range.value);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                dateRange === range.value
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
              data-test-id={`common-date-range-${range.value}`}
            >
              {range.label}
              {dateRange === range.value && <Check className="h-4 w-4" />}
            </button>
          ))}
        </div>

        <hr className="my-1" />

        <div className="p-2">
          <p className="text-[11px] font-bold text-gray-400 uppercase px-1">{t('common.dateRange.customSection')}</p>
          <div className="flex items-center gap-2 p-1.5 bg-gray-50 rounded-2xl border border-gray-100">
            <div className="relative flex-1">
              <label htmlFor="startDate" className="text-[11px] font-bold text-gray-400">{t('common.dateRange.startDate')}</label>
              <input
                type="date"
                value={startDate}
                onChange={event => onStartDateChange(event.target.value)}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="w-full text-[11px] font-bold bg-transparent border-0 focus:ring-0 p-1 text-gray-700 cursor-pointer"
                data-test-id="common-start-date-input"
              />
            </div>

            <ArrowRight className="h-3 w-3 text-gray-300" />

            <div className="relative flex-1">
              <label htmlFor="endDate" className="text-[11px] font-bold text-gray-400">{t('common.dateRange.endDate')}</label>
              <input
                type="date"
                value={endDate}
                onChange={event => onEndDateChange(event.target.value)}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="w-full text-[11px] font-bold bg-transparent border-0 focus:ring-0 p-1 text-gray-700 cursor-pointer"
                data-test-id="common-end-date-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
